import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import Modal from "@/components/Common/Modal/Modal";
import { withTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import loader from "@/images/loader.gif";
import NoPermission from "@/components/NoPermission";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Typography,
    Box,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    Stack,
    Tooltip,
} from "@mui/material";
import MultiEmailInvite from "@/components/MultiEmailInvite";
import AddIcon from "@mui/icons-material/Add";
import { createProjectInvitation } from "@/utils/api-calls";
import { fetchTeammateInvitations } from "@/store/projectData/projectDataActions";

interface RoleType {
    id: number;
    label: string;
    description: string;
    level: number;
    is_assignable: boolean;
}

interface InviteTeammateProps {
    close: () => void;
    isOpen: boolean;
    projectId: number;
    t: (key: string) => string;
}

interface InviteFormValues {
    role: number;
}

const InviteTeammate = ({ close, t, isOpen, projectId }: InviteTeammateProps) => {
    const dispatch = useDispatch();

    // Derive project name from the project store entry matching this projectId
    const projectName: string | undefined = useSelector((state: any) =>
        state.project.find((p: any) => p?.get("id") === projectId)?.get("name")
    );

    const allRoleTypes: RoleType[] = useSelector(
        (state: any) => state.appData.userProjectRoleTypes
    ).toJS();

    // Determine the current user's permission level in this project
    const userPermissionLevel: number | null = useSelector((state: any) => {
        const { projectData, appData, user } = state;
        const userRole = projectData.teammates.find(
            (tm: any) =>
                tm.get("user_id") === user.get("id") &&
                tm.get("project_id") === projectId
        );
        return userRole
            ? appData.userProjectRoleTypes
                .find((uprt: any) => uprt.get("id") === userRole.get("role_type_id"))
                ?.get("level") ?? null
            : null;
    });

    // Default to the 'Team Member' role (level 50) when opening the form.
    // Fall back to 3 so the Select is always controlled from the first render,
    // preventing the uncontrolled→controlled warning when allRoleTypes loads late.
    const defaultRole =
        allRoleTypes.find((rt) => rt.level === 50)?.id ?? allRoleTypes[0]?.id ?? 3;

    const { control, watch } = useForm<InviteFormValues>({
        defaultValues: { role: defaultRole },
    });

    // Watch the role field so handleSubmit always uses the current selection
    const role = watch("role");

    const handleSubmit = useCallback(
        async (list: any) => {
            const response = await createProjectInvitation(projectId, list, role);
            dispatch(fetchTeammateInvitations());
            return response;
        },
        [projectId, role, dispatch]
    );

    // Renders a description for each role type inside the info tooltip
    const TooltipContent = () => (
        <Box sx={{ mt: -2 }}>
            {allRoleTypes.map((roleType) => (
                <React.Fragment key={roleType.id}>
                    <Typography
                        sx={{
                            fontWeight: "bold",
                            mt: 2
                        }}>
                        {t(roleType.label)}
                    </Typography>
                    <Typography>{t(roleType.description)}</Typography>
                </React.Fragment>
            ))}
        </Box>
    );

    // Show a loading spinner while the user's permission level is being resolved
    if (!userPermissionLevel) {
        return (
            <SEO title={projectName}>
                <Modal open={isOpen} onClose={() => close()}>
                    <img src={loader} alt="loading" />
                </Modal>
            </SEO>
        );
    }

    // Only users with a permission level of 50+ may invite teammates
    if (userPermissionLevel < 50) {
        return (
            <SEO title={projectName}>
                <Modal open={isOpen} onClose={() => close()}>
                    <NoPermission />
                </Modal>
            </SEO>
        );
    }

    return (
        <SEO title={projectName}>
            <Modal
                open={isOpen}
                title={
                    <Typography variant="h1" align="center">
                        {projectName}
                    </Typography>
                }
                onClose={() => close()}
                fullWidth
                maxWidth="md"
            >
                {/* No width here — the dialog owns it. */}
                <Box sx={{ mt: -2 }}>
                    <Typography variant="h2" align="center" sx={{
                        fontWeight: "bold"
                    }}>
                        {t("Invite Your Teammates")}
                    </Typography>
                    <Typography color="primary" align="center">
                        {t("Teammates can take challenges and strengthen your project.")}
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            sx={{
                                fontWeight: "bold",
                                mb: 2
                            }}>
                            {t("Invite People")}
                        </Typography>
                        <Box sx={{ width: "100%" }}>
                            <MultiEmailInvite
                                inputFieldSize="normal"
                                placeholder={t("Add Email(s)")}
                                btnText={t("Invite Teammates")}
                                reverse
                                onSubmitRequest={handleSubmit}
                                children={null}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AddIcon />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        minWidth: 0,
                                        background: (theme: any) => theme?.palette?.common?.white,
                                    },
                                }}
                                additionalField={
                                    <Stack
                                        direction="row"
                                        sx={{
                                            alignItems: "center",
                                            width: "400px",
                                            maxWidth: "100%"
                                        }}>
                                        <FormControl fullWidth>
                                            {/* Role selector — managed by react-hook-form */}
                                            <Controller
                                                name="role"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select {...field} sx={{ minWidth: 0 }}>
                                                        {allRoleTypes.map((roleType) => (
                                                            <MenuItem
                                                                key={roleType.id}
                                                                value={roleType.id}
                                                                // Disable roles the current user cannot assign
                                                                disabled={roleType.level > userPermissionLevel}
                                                            >
                                                                {t(roleType.label)}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                )}
                                            />
                                        </FormControl>
                                        <Tooltip
                                            title={<TooltipContent />}
                                            enterTouchDelay={0}
                                            slotProps={{
                                                popper: { placement: "right" }
                                            }}
                                        >
                                            <InfoOutlinedIcon sx={{ height: 16, width: 16, ml: 1 }} />
                                        </Tooltip>
                                    </Stack>
                                }
                            />
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </SEO>
    );
};

export default withTranslation(["translation.InviteTeammate"])(InviteTeammate);
